// 端末・認証器セキュリティの API (#1138): WebAuthn(Passkey)/端末承認/SMS復旧/TOTP。
// 顧客・店舗どちらの認証器もここ。配置ルールは index.ts のヘッダ参照。
import { getDeviceId } from '../../lib/deviceId'
import { ApiError, apiFetch } from './_client'

// #734: WebAuthn / Passkey types
export interface WebAuthnConfig {
  enrollmentMode: 'disabled' | 'optional' | 'required_new_users' | 'required_all'
  rpId: string
  // #815: WebAuthn 不可層 TOTP fallback の有効可否 (既定 false / 旧 backend は欠落 → undefined)
  totpFallbackEnabled?: boolean
  // #819 Phase 2b: 初見 device 承認 gate の有効可否 (既定 false / 旧 backend は欠落 → undefined)
  deviceApprovalEnabled?: boolean
  // #2663 お店のオーナーの 2 段階認証 (TOTP) の必須化。optional (任意) / required_all (全員必須)。旧 backend は欠落 → optional 扱い
  storeTotpMode?: 'optional' | 'required_all'
}

export interface WebAuthnCredentialItem {
  credentialId: string
  deviceLabel: string | null
  customLabel?: string | null
  credentialDeviceType?: string
  createdAt: unknown
  lastUsedAt: unknown
  transports: string[]
}

// #791 ②: 未確認の新規 device 登録 (next-login 本人確認用)
export interface PendingDeviceConfirmation {
  credentialId: string
  deviceLabel: string
  ipAddress: string
  enrolledAt: string | null
}

export const devicesApi = {
  // #734 WebAuthn / Passkey
  getWebauthnConfig: async (): Promise<WebAuthnConfig> => {
    const res = await apiFetch('/webauthn/config', { method: 'GET' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to fetch webauthn config')
    return res.json()
  },
  webauthnRegisterBegin: async (): Promise<{ options: unknown; challengeId: string }> => {
    const res = await apiFetch('/webauthn/register/begin', { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to begin registration')
    return res.json()
  },
  webauthnRegisterComplete: async (
    challengeId: string,
    credential: unknown,
    deviceLabel?: string,
  ): Promise<{ credentialId: string; deviceLabel: string; credentialDeviceType?: string }> => {
    // #1106: device-id を同送。端末バインド(single_device)登録なら backend が
    // known_device doc に anchorCredentialId を刻む(揮発 → self-heal 時の
    // ゾンビ端末 GC 用。trust 状態は不変・flag OFF では無作用)。
    const res = await apiFetch('/webauthn/register/complete', {
      method: 'POST',
      body: JSON.stringify({ challengeId, credential, deviceLabel, deviceId: getDeviceId() }),
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => 'register failed'))
    return res.json()
  },
  webauthnAuthBegin: async (): Promise<{ options: unknown; challengeId: string }> => {
    const res = await apiFetch('/webauthn/auth/begin', { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to begin authentication')
    return res.json()
  },
  webauthnAuthComplete: async (
    challengeId: string,
    credential: unknown,
  ): Promise<{ credentialId: string; authLogId: string; deviceTrusted?: boolean }> => {
    // #837 refined-(a): このブラウザの device-id を同送。backend は
    // DEVICE_APPROVAL_ENABLED ON かつ端末バインド(single_device)passkey の
    // ときだけ device-id を自動 trusted 化し、localStorage 揮発を自己修復
    // (flag OFF / 同期 passkey では無視 = 挙動不変)。
    const res = await apiFetch('/webauthn/auth/complete', {
      method: 'POST',
      body: JSON.stringify({ challengeId, credential, deviceId: getDeviceId() }),
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => 'auth failed'))
    return res.json()
  },
  listWebauthnCredentials: async (): Promise<{ credentials: WebAuthnCredentialItem[] }> => {
    const res = await apiFetch('/webauthn/credentials', { method: 'GET' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to list credentials')
    return res.json()
  },
  deleteWebauthnCredential: async (credentialId: string): Promise<{ deleted: boolean }> => {
    const res = await apiFetch(`/webauthn/credentials/${encodeURIComponent(credentialId)}`, {
      method: 'DELETE',
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to delete credential')
    return res.json()
  },
  // #1114+: passkey に任意の名前を付ける(ランダム ID では区別しづらいため)。
  renameWebauthnCredential: async (
    credentialId: string,
    label: string,
  ): Promise<{ renamed: boolean }> => {
    const res = await apiFetch(`/webauthn/credentials/${encodeURIComponent(credentialId)}/label`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to rename credential')
    return res.json()
  },
  // #867: 店舗オーナー向け WebAuthn (opt-in, TOTP と並列の 2要素)。
  // backend `routers/store_webauthn.py` の alias router を叩く。データは
  // `users/{firebase_uid}/credentials/...` に保存(顧客 `user_xxxxx` と uid
  // format 差で衝突なし)。
  storeWebauthnRegisterBegin: async (): Promise<{ options: unknown; challengeId: string }> => {
    const res = await apiFetch('/store-webauthn/register/begin', { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to begin store registration')
    return res.json()
  },
  storeWebauthnRegisterComplete: async (
    challengeId: string,
    credential: unknown,
    deviceLabel?: string,
  ): Promise<{ credentialId: string; deviceLabel: string }> => {
    // #1106: device-id を同送 (顧客側と対称。anchor → ゾンビ端末 GC 用)。
    const res = await apiFetch('/store-webauthn/register/complete', {
      method: 'POST',
      body: JSON.stringify({ challengeId, credential, deviceLabel, deviceId: getDeviceId() }),
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => 'store register failed'))
    return res.json()
  },
  storeWebauthnAuthBegin: async (): Promise<{ options: unknown; challengeId: string }> => {
    const res = await apiFetch('/store-webauthn/auth/begin', { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to begin store authentication')
    return res.json()
  },
  storeWebauthnAuthComplete: async (
    challengeId: string,
    credential: unknown,
  ): Promise<{ credentialId: string; authLogId: string; deviceTrusted?: boolean }> => {
    // #837 refined-(a) for store: deviceId を同送 → backend は端末バインド
    // passkey なら device-id を自動 trusted 化(店舗 device-approval gate
    // の自動解除)。
    const res = await apiFetch('/store-webauthn/auth/complete', {
      method: 'POST',
      body: JSON.stringify({ challengeId, credential, deviceId: getDeviceId() }),
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => 'store auth failed'))
    return res.json()
  },
  listStoreWebauthnCredentials: async (): Promise<{ credentials: WebAuthnCredentialItem[] }> => {
    const res = await apiFetch('/store-webauthn/credentials', { method: 'GET' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to list store credentials')
    return res.json()
  },
  deleteStoreWebauthnCredential: async (credentialId: string): Promise<{ deleted: boolean }> => {
    const res = await apiFetch(`/store-webauthn/credentials/${encodeURIComponent(credentialId)}`, {
      method: 'DELETE',
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to delete store credential')
    return res.json()
  },
  // #867: 店舗オーナー向け Device approval gate (顧客 #819 mirror)
  storeRegisterDevice: async (
    deviceId: string,
    deviceLabel?: string,
    viaTotp: boolean = false,
  ): Promise<{ deviceId: string; isNewDevice: boolean; trusted: boolean }> => {
    // #869c: viaTotp=true は Firebase MFA で TOTP 強要素を直前に通過済 = backend
    // が auto-trust する(refined-(a) と同等)。
    const res = await apiFetch('/store-devices/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId, deviceLabel: deviceLabel || null, viaTotp }),
    })
    return res.json()
  },
  getStoreDeviceStatus: async (
    deviceId: string,
  ): Promise<{ deviceId: string; exists: boolean; trusted: boolean; denied: boolean }> => {
    const res = await apiFetch(
      `/store-devices/status?deviceId=${encodeURIComponent(deviceId)}`,
      { method: 'GET' },
    )
    return res.json()
  },
  listStorePendingDevices: async (
    callerDeviceId: string,
  ): Promise<{
    pending: Array<{
      deviceId: string
      deviceLabel?: string | null
      firstSeen?: unknown
      lastIpAddress?: string
      lastUserAgent?: string
      deniedAt?: unknown
    }>
  }> => {
    const res = await apiFetch(
      `/store-devices/pending?callerDeviceId=${encodeURIComponent(callerDeviceId)}`,
      { method: 'GET' },
    )
    return res.json()
  },
  approveStoreDevice: async (
    targetDeviceId: string,
    callerDeviceId: string,
  ): Promise<{ approved: boolean; deviceId: string }> => {
    const res = await apiFetch('/store-devices/approve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetDeviceId, callerDeviceId }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to approve store device')
    return res.json()
  },
  denyStoreDevice: async (
    targetDeviceId: string,
    callerDeviceId: string,
  ): Promise<{ denied: boolean; deviceId: string; deletedCredentials: number }> => {
    const res = await apiFetch('/store-devices/deny', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetDeviceId, callerDeviceId }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to deny store device')
    return res.json()
  },
  listStoreDevices: async (): Promise<{
    devices: Array<{
      id: string
      deviceId: string
      deviceLabel?: string | null
      // #1940 オーナーが付けた名前 (device_labels から join されて返る)
      customLabel?: string | null
      trusted?: boolean
      deniedAt?: unknown
      firstSeen?: unknown
      lastSeen?: unknown
      lastIpAddress?: string
      lastUserAgent?: string
    }>
  }> => {
    // #1928 呼び元の端末を伝える。**未信頼の端末には他の端末を返さない** —
    // 以前は誰でも全件取れたため、信頼済み端末の ID を拾って callerDeviceId に
    // 詰めれば自分を承認でき、gate が素通しだった。
    const res = await apiFetch(
      `/store-devices/list?callerDeviceId=${encodeURIComponent(getDeviceId())}`,
      { method: 'GET' },
    )
    return res.json()
  },
  revokeStoreDevice: async (
    targetDeviceId: string,
    callerDeviceId: string,
  ): Promise<{ revoked: boolean; deviceId: string; result: string }> => {
    const res = await apiFetch('/store-devices/revoke', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetDeviceId, callerDeviceId }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to revoke store device')
    return res.json()
  },
  // #1940 端末に名前を付ける。`ID 1cf66de5` の断片では、どれが店の PC で
  // どれが自分のスマホか分からない (同じ端末の記録が複数できるため)。
  // 授権は承認/解除と同じく信頼済み端末からのみ (backend が 403)。
  renameStoreDevice: async (
    targetDeviceId: string,
    callerDeviceId: string,
    label: string,
  ): Promise<{ renamed: boolean; deviceId: string }> => {
    const res = await apiFetch('/store-devices/rename', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetDeviceId, callerDeviceId, label }),
    })
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => 'rename failed'))
    return res.json()
  },
  resetWebauthnCredentials: async (): Promise<{ deletedCount: number }> => {
    const res = await apiFetch('/webauthn/recovery/reset', { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to reset credentials')
    return res.json()
  },
  // #791 ②: 新規 device 登録の本人確認
  getPendingDeviceConfirmation: async (): Promise<{ pending: PendingDeviceConfirmation[] }> => {
    const res = await apiFetch('/webauthn/pending-device-confirmation', { method: 'GET' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to fetch pending device confirmation')
    return res.json()
  },
  confirmDevice: async (
    credentialId: string,
    action: 'acknowledge' | 'revoke',
  ): Promise<{ acknowledged?: boolean; revoked?: boolean; deletedCount?: number }> => {
    const res = await apiFetch('/webauthn/confirm-device', {
      method: 'POST',
      body: JSON.stringify({ credentialId, action }),
      // Content-Type 必須: 無いと fetch が text/plain を送り FastAPI が
      // ConfirmDeviceRequest を JSON parse せず 422 になる (#791 ② が機能不全に
      // なるバグ。他の JSON POST と同じく明示する)。
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to confirm device')
    return res.json()
  },
  // #815: WebAuthn 不可層 TOTP enroll の離脱率計測 (best-effort、auth-gated)。
  // 'started' = enforced modal 到達 / 'completed' = enroll 成功。
  // #816 (SMS-domestic) の data-driven 着手トリガに使う。
  recordTotpEnrollEvent: async (stage: 'started' | 'completed'): Promise<void> => {
    await apiFetch('/mfa/totp-metric', {
      method: 'POST',
      body: JSON.stringify({ stage }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  // #819 Track B-main Phase 1: 端末を既知 device として記録 (auth-gated)。
  // best-effort で login 後に呼ぶ。返り: { deviceId, isNewDevice, trusted }。
  // Phase 1 は記録のみ(gate なし)。Firebase 認証経路でのみ成立、
  // demo/web3 は token 無で 401 → 呼び元が握り潰す。
  // #869c: viaTotp=true は Firebase MFA で TOTP 強要素を直前に通過済 = backend
  // が WebAuthn refined-(a) と同等に device を auto-trust する。
  registerDevice: async (
    deviceId: string,
    deviceLabel?: string,
    viaTotp: boolean = false,
  ): Promise<{ deviceId: string; isNewDevice: boolean; trusted: boolean }> => {
    const res = await apiFetch('/devices/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId, deviceLabel: deviceLabel || null, viaTotp }),
    })
    return res.json()
  },

  // #819 Track B-main Phase 2b: 初見 device 承認 gate (backend は Phase 2a)。
  // status = この端末が trusted か(新端末が承認待ちで poll)。
  getDeviceStatus: async (
    deviceId: string,
  ): Promise<{ deviceId: string; exists: boolean; trusted: boolean; denied: boolean; firstSeen?: number | null; graceSeconds?: number | null; hasOtherTrusted?: boolean }> => {
    const res = await apiFetch(
      `/devices/status?deviceId=${encodeURIComponent(deviceId)}`,
      { method: 'GET' },
    )
    return res.json()
  },

  // #1114: 猟予期タイムアウト自動承認。firstSeen 経過を server が検証してから
  // trusted 化(client タイマーは信用されない)。到達可能な trusted 端末が無く
  // operator しか出口が無い構造的ロックアウトの自己復旧。
  graceApproveDevice: async (
    deviceId: string,
  ): Promise<{ trusted: boolean; result: string }> => {
    const res = await apiFetch('/devices/grace-approve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId }),
    })
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => 'grace-approve failed'))
    return res.json()
  },

  // ============================================================
  // #1107: SMS 端末復旧 (flag SMS_DEVICE_RECOVERY_ENABLED、既定 OFF)
  // ============================================================

  // SMS 復旧の可否 + 登録済み番号(マスク)。untrusted 端末(待機 modal)からも可。
  getRecoveryPhoneStatus: async (): Promise<{
    enabled: boolean
    registered: boolean
    maskedNumber: string | null
  }> => {
    const res = await apiFetch('/devices/recovery-phone/status', { method: 'GET' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to fetch recovery phone status')
    return res.json()
  },

  // 復旧用電話番号を登録。事前に Firebase linkWithPhoneNumber で SMS 検証済で
  // あること(backend が ID token の phone_number claim と一致検証)。
  // trusted device からのみ。
  registerRecoveryPhone: async (
    phoneNumber: string,
    callerDeviceId: string,
  ): Promise<{ registered: boolean; maskedNumber: string }> => {
    const res = await apiFetch('/devices/recovery-phone', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phoneNumber, callerDeviceId }),
    })
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => 'register failed'))
    return res.json()
  },

  // 復旧用電話番号を解除。trusted device からのみ。
  deleteRecoveryPhone: async (
    callerDeviceId: string,
  ): Promise<{ deleted: boolean }> => {
    const res = await apiFetch('/devices/recovery-phone/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callerDeviceId }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to delete recovery phone')
    return res.json()
  },

  // 復旧 SMS 送信の事前認可 (typed 番号 == 登録済み番号 + rate limit 3/日)。
  // 200 が返るまで client は Firebase SDK の SMS 送信を行わない。
  beginRecoverySms: async (
    phoneNumber: string,
  ): Promise<{ ok: boolean }> => {
    const res = await apiFetch('/devices/recovery-sms/begin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phoneNumber }),
    })
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => 'begin failed'))
    return res.json()
  },

  // SMS reauth 完了後の device trust。backend が ID token の
  // sign_in_provider=='phone' + phone_number claim 一致を検証。
  trustDeviceViaSms: async (
    deviceId: string,
  ): Promise<{ trusted: boolean; deviceId: string }> => {
    const res = await apiFetch('/devices/trust-via-sms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId }),
    })
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => 'trust failed'))
    return res.json()
  },

  // 承認待ち(未信頼)device 一覧。**trusted device からのみ** (403 if not)。
  listPendingDevices: async (
    callerDeviceId: string,
  ): Promise<{
    pending: Array<{
      deviceId: string
      deviceLabel?: string | null
      firstSeen?: unknown
      lastIpAddress?: string
      lastUserAgent?: string
      deniedAt?: unknown
    }>
  }> => {
    const res = await apiFetch(
      `/devices/pending?callerDeviceId=${encodeURIComponent(callerDeviceId)}`,
      { method: 'GET' },
    )
    return res.json()
  },

  // 新 device を承認 (trusted 化)。caller は trusted device 必須。
  approveDevice: async (
    targetDeviceId: string,
    callerDeviceId: string,
  ): Promise<{ approved: boolean; deviceId: string }> => {
    const res = await apiFetch('/devices/approve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetDeviceId, callerDeviceId }),
    })
    return res.json()
  },

  // 新 device を否認 = 心当たり無し → 全 credential 失効(凍結)。
  denyDevice: async (
    targetDeviceId: string,
    callerDeviceId: string,
  ): Promise<{ denied: boolean; deviceId: string; deletedCredentials: number }> => {
    const res = await apiFetch('/devices/deny', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetDeviceId, callerDeviceId }),
    })
    return res.json()
  },

  // #841: 既知 device 全件(管理画面用)。trusted/pending/denied を含む。
  listDevices: async (): Promise<{
    devices: Array<{
      deviceId?: string
      id?: string
      deviceLabel?: string | null
      trusted?: boolean
      deniedAt?: unknown
      revokedAt?: unknown
      firstSeen?: unknown
      lastSeen?: unknown
      lastIpAddress?: string
    }>
  }> => {
    // #1928 店舗側と同じく呼び元の端末を伝える (未信頼には他の端末を返さない)
    const res = await apiFetch(
      `/devices/list?callerDeviceId=${encodeURIComponent(getDeviceId())}`,
      { method: 'GET' },
    )
    if (!res.ok) throw new ApiError(res.status, 'failed to list devices')
    return res.json()
  },

  // #841: trusted 端末を self-service で soft 解除(deny の凍結とは別)。
  // caller は trusted device 必須。最後の trusted は 409(#837 lockout
  // guard)— 呼び元は ApiError.status===409 を捕捉して文言出し分け。
  revokeDevice: async (
    targetDeviceId: string,
    callerDeviceId: string,
  ): Promise<{ revoked: boolean; deviceId: string; result: string }> => {
    const res = await apiFetch('/devices/revoke', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetDeviceId, callerDeviceId }),
    })
    if (!res.ok) {
      throw new ApiError(
        res.status,
        await res.text().catch(() => 'revoke failed'),
      )
    }
    return res.json()
  },

  // #1107 follow-up: known_device を記録ごと hard delete(revoke の soft
  // untrust だと承認待ちに再出現するため)。caller は trusted device 必須。
  // 最後の trusted は 409(#837 lockout guard)。
  deleteDevice: async (
    targetDeviceId: string,
    callerDeviceId: string,
  ): Promise<{ deleted: boolean; deviceId: string }> => {
    const res = await apiFetch('/devices/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetDeviceId, callerDeviceId }),
    })
    if (!res.ok) {
      throw new ApiError(
        res.status,
        await res.text().catch(() => 'delete failed'),
      )
    }
    return res.json()
  },

  // #1114 E: 端末に任意のニックネームを付ける(数値IDでなく名前で識別)。
  // caller は trusted device 必須。
  renameDevice: async (
    targetDeviceId: string,
    callerDeviceId: string,
    label: string,
  ): Promise<{ renamed: boolean; deviceId: string }> => {
    const res = await apiFetch('/devices/rename', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetDeviceId, callerDeviceId, label }),
    })
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => 'rename failed'))
    return res.json()
  },
}
